"use client";

import { useEffect } from "react";
/* The boundary renders its own document, so it cannot inherit the styles the
   root layout loads — it imports the design system itself. Tokens + base give
   it the type scale, colours and .wrap/.card/.btn primitives; states.css gives
   it the terminal status-card recipe every other dead-end screen already uses
   (08-organisms.md#empty-loading-error-states). */
import "@/styles/ds/tokens.css";
import "@/styles/ds/base.css";
import "./_components/states.css";
import { isStaleChunkError } from "@/lib/staleChunkError";

/** Reload once, then stay put for a while — a candidate can legitimately hit
 * this twice in one tab (two deploys landing hours apart in one long sitting),
 * but if a hard reload keeps landing back here within seconds the server
 * itself is broken, and looping would just hide that behind a blank screen
 * that keeps flashing and reloading. */
const RELOAD_GUARD_KEY = "pl.v2.chunkErrorReloadedAt";
const RELOAD_GUARD_WINDOW_MS = 15_000;

/**
 * Root error boundary. Also works around a Next 16.2.10 Turbopack bug where the
 * built-in global-error module isn't found in the RSC client manifest — giving
 * the app its own module resolves it. Must render its own <html>/<body>.
 *
 * The icon is inlined rather than imported: this is the screen that renders
 * when module loading has already gone wrong, so it carries as little of the
 * app with it as it can.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error(error);
    if (!isStaleChunkError(error)) return;
    try {
      const lastReload = Number(sessionStorage.getItem(RELOAD_GUARD_KEY));
      if (lastReload && Date.now() - lastReload < RELOAD_GUARD_WINDOW_MS) return;
      sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
    } catch {
      // Storage disabled — reload anyway; worst case is one extra reload.
    }
    window.location.reload();
  }, [error]);

  return (
    <html lang="en">
      <body>
        <main>
          <div className="wrap">
            <div className="card state-card">
              <span className="state-icon is-error">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true" focusable="false">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 8v5" />
                  <path d="M12 16.5v.01" />
                </svg>
              </span>
              <h1>Something went wrong</h1>
              <p>An unexpected error occurred. Please try again.</p>
              <button type="button" className="btn btn-primary" onClick={() => reset()}>
                Try again
              </button>
            </div>
          </div>
        </main>
      </body>
    </html>
  );
}
